import { roundWithLargestRemainder } from '@/utils/currencyRounding';
import { countEligibleServiceChargeDays } from '@/utils/serviceChargeUtils';
import type { DayAnalytics } from '@/services/timeEntries/types';

/**
 * Version marker — bump whenever the core algorithm changes.
 * SYNC: Must match supabase/functions/_shared/serviceCharge.ts CORE_VERSION.
 */
export const CORE_VERSION = 1;

// ─── Input / Output types ───────────────────────────────────────────────

/** Minimal analytics slice needed by the core allocator. */
export interface ServiceChargeAnalyticsSlice {
  dayAnalytics: DayAnalytics[] | undefined;
  totalWorkedDays: number;
}

/** Per-employee input fed into the core allocator. */
export interface ServiceChargeEmployeeInput {
  profileId: string;
  name: string;
  analytics: ServiceChargeAnalyticsSlice;
}

/** Weight definition (mirrors DB row shape). */
export interface ServiceChargeWeightDef {
  id: string;
  weight_label: string;
  weight_multiplier: number;
}

/** Full structured input to the pure core allocator. */
export interface ServiceChargeAllocationInput {
  employees: ServiceChargeEmployeeInput[];
  /** Map of profileId → weightId */
  assignmentMap: Map<string, string>;
  weights: ServiceChargeWeightDef[];
  staffAllocationAmount: number;
  excludeDaysBelowHours: number | null;
}

/** Per-employee allocation result. */
export interface ServiceChargeAllocation {
  profileId: string;
  name: string;
  workingDays: number;
  weightMultiplier: number;
  weightLabel: string;
  weightedDays: number;
  distributionAmount: number;
}

/** Complete result from the core allocator. */
export interface ServiceChargeAllocationResult {
  allocations: ServiceChargeAllocation[];
  totalWeightedDays: number;
}

// ─── Pure core allocator ────────────────────────────────────────────────

/**
 * Computes service charge allocations for a set of employees.
 *
 * Pure function — no DB calls, no side effects.
 * Uses `countEligibleServiceChargeDays` for day filtering and
 * `roundWithLargestRemainder` for cent-exact distribution.
 *
 * SYNC: An identical implementation exists in
 *       supabase/functions/_shared/serviceCharge.ts for edge functions.
 *       Keep both in sync; bump CORE_VERSION on changes.
 */
export function computeServiceChargeAllocations(
  input: ServiceChargeAllocationInput
): ServiceChargeAllocationResult {
  const { employees, assignmentMap, weights, staffAllocationAmount, excludeDaysBelowHours } = input;

  // Build weight lookup
  const weightMap = new Map(weights.map(w => [w.id, w]));

  // Phase 1: compute weighted days per employee
  let totalWeightedDays = 0;
  const eligible: Array<{
    profileId: string;
    name: string;
    workingDays: number;
    weightMultiplier: number;
    weightLabel: string;
    weightedDays: number;
  }> = [];

  for (const emp of employees) {
    const weightId = assignmentMap.get(emp.profileId);
    if (!weightId) continue; // skip employees without weight assignment

    const weight = weightMap.get(weightId);
    if (!weight) continue; // skip if weight definition missing

    const workingDays = countEligibleServiceChargeDays(
      emp.analytics.dayAnalytics,
      emp.analytics.totalWorkedDays,
      excludeDaysBelowHours
    );

    const weightedDays = workingDays * weight.weight_multiplier;
    totalWeightedDays += weightedDays;

    eligible.push({
      profileId: emp.profileId,
      name: emp.name,
      workingDays,
      weightMultiplier: weight.weight_multiplier,
      weightLabel: weight.weight_label,
      weightedDays,
    });
  }

  // Phase 2: if no weighted days, all amounts are 0 — skip rounding entirely
  if (totalWeightedDays <= 0) {
    const allocations: ServiceChargeAllocation[] = eligible.map(e => ({
      ...e,
      distributionAmount: 0,
    }));
    return { allocations, totalWeightedDays: 0 };
  }

  // Phase 3: compute raw proportional amounts
  const rawAmounts = eligible.map(e =>
    (e.weightedDays / totalWeightedDays) * staffAllocationAmount
  );

  // Phase 4: apply largest-remainder rounding for cent-exact totals
  const roundedAmounts = roundWithLargestRemainder(rawAmounts, staffAllocationAmount, 2);

  // Phase 4: build results
  const allocations: ServiceChargeAllocation[] = eligible.map((e, i) => ({
    ...e,
    distributionAmount: roundedAmounts[i],
  }));

  return { allocations, totalWeightedDays };
}
